import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobeIcon, SmartphoneIcon, ZapIcon, TargetIcon, SearchIcon, ArrowRightIcon, CheckCircleIcon, CodeIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
import { SEOHead } from '../components/SEOHead';
export function WebDesignSeoPage() {
  const faqs = [{
    question: 'Do I need a new website to work with you?',
    answer: 'Not necessarily. If your current website is fast, mobile-friendly, and converts well, we can often just implement our AI lead conversion tools and ad management. However, if your site is slow or outdated, a new conversion-focused site will significantly improve your ad ROI.'
  }, {
    question: 'What makes your websites different from a standard template?',
    answer: 'We build websites specifically as conversion assets for home service businesses. They are designed to load instantly, look perfect on mobile, and guide visitors directly toward booking an estimate or making a call.'
  }, {
    question: 'Do you provide ongoing SEO services?',
    answer: 'Yes, but we focus on local SEO and GEO (Generative Engine Optimization) as a support layer for discoverability. We ensure your site is structured correctly, loads fast, and has crawlable service content so local customers and AI search engines can find you.'
  }, {
    question: 'How long does it take to build a new website?',
    answer: 'A standard conversion website typically takes 2-4 weeks from kickoff to launch, depending on how quickly we receive your branding assets and approvals.'
  }, {
    question: 'Are your websites mobile-friendly?',
    answer: 'Yes. Over 70% of local service searches happen on mobile devices. We design mobile-first, ensuring that click-to-call buttons and lead forms are immediately accessible on small screens.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2611">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2612">
          <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-2613">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-2614" />
        </button>
        <AnimatePresence data-id="element-2615">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2616">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2617">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2618">
      <SEOHead title="Web Design & Local SEO | Conversion Websites | Viridia Analytics" description="We build fast, mobile-first websites designed to convert local traffic into booked jobs. Supported by local SEO and GEO for discoverability." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, marketing agency phoenix, contractor websites, HVAC websites, painter websites, landscaper websites, window installer websites, mobile-first website, conversion-focused design, local search optimization, website development Phoenix" faq={faqs} data-id="element-2619" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2620">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2621">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-2622"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2623">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2624">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2625">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-2626">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-2627">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-2628">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-2629">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2630">
                  Your website should not just be a digital brochure. We build
                  fast, mobile-first websites that act as conversion
                  infrastructure, supported by local SEO to ensure you are found
                  when customers are searching.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2631">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-2632">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2633">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-2634">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-2635">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-2636">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2637">
                    <div className="flex space-x-2" data-id="element-2638">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2639"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2640"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2641"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2642">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2643" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-2644" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2645">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2646">
          <ScrollReveal data-id="element-2647">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2648">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2649">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2650">
                Not just pretty - built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2651">
            <ScrollReveal delay={0.1} data-id="element-2652">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2653">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2654">
                  <ZapIcon className="w-7 h-7" data-id="element-2655" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2656">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2657">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2658">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2659">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2660">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-2661" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2662">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2663">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-2664">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2665">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2666">
                  <TargetIcon className="w-7 h-7" data-id="element-2667" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2668">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2669">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SEO & GEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2670">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2671">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2672">
            <ScrollReveal data-id="element-2673">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2674">
                Discoverability Support
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-2675">
                A great website is useless if no one can find it. We provide
                local SEO and GEO (Generative Engine Optimization) support to
                ensure your business is visible when it matters.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-2676">
                <li className="flex items-start" data-id="element-2677">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-2678" />
                  <span className="text-gray-300" data-id="element-2679">
                    Crawlable service content for search engines
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2680">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-2681" />
                  <span className="text-gray-300" data-id="element-2682">
                    Local relevance optimization (Google Business Profile
                    integration)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2683">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-2684" />
                  <span className="text-gray-300" data-id="element-2685">
                    Structured data (Schema markup) for AI discoverability
                  </span>
                </li>
              </ul>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-2686">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-2687">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-800" data-id="element-2688">
                  <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center" data-id="element-2689">
                    <CodeIcon className="w-6 h-6 text-viridia" data-id="element-2690" />
                  </div>
                  <div data-id="element-2691">
                    <h3 className="text-xl font-bold text-white" data-id="element-2692">
                      Technical Foundation
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-2693">
                      Built for modern search
                    </p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2694">
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2695">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-2696">
                      Semantic HTML
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-2697">
                      Clear heading structures and accessible markup.
                    </div>
                  </div>
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-2698">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-2699">
                      Fast Hosting
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-2700">
                      Deployed on global edge networks for instant load times.
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-2701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2702">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2703">
            <ScrollReveal data-id="element-2704">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2705">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2706">
                Our web design services are for local service businesses that
                need a strong digital foundation to support their ad campaigns
                and lead conversion efforts.
              </p>
              <ul className="space-y-4" data-id="element-2707">
                <li className="flex items-start gap-3" data-id="element-2708">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2709" />
                  <div data-id="element-2710">
                    <h4 className="font-bold text-white" data-id="element-2711">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-2712">
                      HVAC, painters, landscapers, window installers, and other
                      businesses that rely on local search to find customers.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2713">
                  <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-2714" />
                  <div data-id="element-2715">
                    <h4 className="font-bold text-white" data-id="element-2716">
                      Businesses Running Ads
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-2717">
                      If you are paying for traffic, you need a website that
                      actually converts that traffic into leads.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-2718">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-2719">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-2720">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-2721">
                  <Link to="/ad-campaigns" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2722">
                    <div className="font-bold text-white mb-1" data-id="element-2723">
                      Ad Campaigns
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2724">
                      Drive high-intent traffic to your new conversion website.
                    </div>
                  </Link>
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2725">
                    <div className="font-bold text-white mb-1" data-id="element-2726">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2727">
                      Ensure every lead generated by your site gets an instant
                      response.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2728">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2729">
          <ScrollReveal data-id="element-2730">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2731">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2732">
          <ScrollReveal delay={0.2} data-id="element-2733">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2734">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2735" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2736">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2737">
          <ScrollReveal data-id="element-2738">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2739">
              Ready for a site that converts?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2740">
              Book a strategy call to see how we can build a conversion-focused
              website for your business.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300" data-id="element-2741">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2742" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}